import authRequest from '@/modules/auth-request.js';
import http from '@/modules/http';
import Receipt from '@/store/models/receipt';
import PaymentMethod from '@/store/models/payment-method';
import { MaintenanceExtensionOption } from '@/store/models/maintenance-option';


const MIN_PASSWORD_LENGTH = 8;

function first(list, method, context) {
	for (var i = 0, len = list && list.length || 0; i < len; i++) {
		if (method.call(context, list[i], i, list)) {
			return list[i];
		}
	}
	return null;
}

function toDollars(cents) {
	return +(cents / 100).toPrecision(15);
}

function isEmpty(obj) {
	return Object.keys(obj).length === 0;
}

export default {

	getAccountInfo() {
		return authRequest.get('ACCOUNT_API://me')
			.then(http.resolveData, http.rejectData)
			.then(function (info) {
				let account = info;

				var balance = toDollars(Math.min(0, info.accountBalance)) || 0;
				var nextPayment = info.estimatedNextReceipt && !isEmpty(info.estimatedNextReceipt) && Receipt.fromDTO(info.estimatedNextReceipt);
				var paymentMethod = PaymentMethod.fromDTO(info.paymentMethod);

				if (nextPayment) {
					nextPayment.remainingCredit = Math.min(0, balance + (nextPayment.subtotal || 0));
				}

				return {
					paymentMethod: paymentMethod,
					nextPayment: nextPayment,
					balance: balance,
					balanceCents: info.accountBalance,
					canAddPaymentMethod: info.canAddPaymentMethod
				};
			});
	},

	getReceipts() {
		return authRequest.get('ACCOUNT_API://me/receipts')
			.then((response) => {
				return { // format response until api returns it the way we want
					data: response.data && response.data.data && response.data.data.receipts || response.data.data || response.data
				};
			})
			.then(http.createListMapper(Receipt.fromDTO), http.rejectData);

	},

	getSubscriptions(payload) {
		return authRequest.get('ACCOUNT_API://subscriptions')
			.then(http.resolveData, http.rejectData);

	},

	changeSubscription(payload) {
		return authRequest.patch('ACCOUNT_API://subscriptions/' + payload.subId + (payload.prepaidCouponId ? '/maintenance' : ''),
			{
				interval: payload.interval,
				planId: payload.id,
				addOns: payload.addOns,
				quantity: payload.quantity,
				prepaidCouponId: payload.prepaidCouponId,
				cancelAtPeriodEnd: payload.cancelAtPeriodEnd,
				transitionAtEnd: payload.transitionAtEnd
			})
			.then(http.resolveData, http.rejectData);
	},

	addSubscription(payload) {
		return authRequest.post('ACCOUNT_API://subscriptions',
			{
				interval: payload.interval,
				planId: payload.id,
				addOns: payload.addOns,
				quantity: payload.quantity,
				trial: payload.trial || undefined,
				prepaidCouponId: payload.prepaidCouponId || undefined,
				cancelAtPeriodEnd: payload.cancelAtPeriodEnd,
				transitionAtEnd: payload.transitionAtEnd
			})
			.then(http.resolveData, http.rejectData);
	},

	getMaintenanceOptions(subscriptionId) {
		return authRequest.get(`ACCOUNT_API://subscriptions/${subscriptionId}/maintenance/options`)
			.then((resp) => {
				const options = resp.data.options;
				const isAtMaxMaintenanceLength = resp.data.details.isAtMaxMaintenanceLength;
				const isOutsideMaintenanceRange = resp.data.details.isOutsideMaintenanceRange;

				if (isAtMaxMaintenanceLength || isOutsideMaintenanceRange) {
					//No options
					return [];
				}

				const extensions = options.map(option => new MaintenanceExtensionOption(option)).sort((a,b) =>  Date.parse(a.newExpiration) - Date.parse(b.newExpiration));
				return extensions;

			}, http.rejectData);
	},

	purchaseMaintenanceExtension(subscriptionId, maintenanceExtensionId, quantity) {
		return authRequest.patch(`ACCOUNT_API://subscriptions/${subscriptionId}/maintenance`, {
			maintenanceExtensionId,
			quantity
		})
			.then((resp) => {
				return resp.data.oneTimePurchaseId;
			});
	},

	getOneTimePaymentResponse(oneTimePurchaseId) {
		return authRequest.get(`ACCOUNT_API://onetimepurchase/${oneTimePurchaseId}`)
			.then((resp) => {
				return {
					accountId: resp.data.accountId,
					created: resp.data.created,
					forSubscriptionId: resp.data.forSubscriptionId,
					id: resp.data.id,
					items: resp.data.items, //[{item: {amount: number, category: string, durationInMonths: number, id: string, name: string}, quantity: number }]
					status: resp.data.status,
					stripeInvoiceId: resp.data.stripeInvoiceId
				};
			});
	},

	getPromotion(payload) {
		return authRequest.get(`ACCOUNT_API://promotion/account?promotionName=${payload}`)
			.then(http.resolveData, http.rejectData);
	},

	addPromotion(payload) {
		return authRequest.post('ACCOUNT_API://promotion', { promoCode: payload.promoCode })
			.then(http.resolveData, http.rejectData);
	},

	removeSubscription(subId) {
		return authRequest.delete('ACCOUNT_API://subscriptions/' + subId)
			.then(http.resolveData, http.rejectData);
	},

	getPlans(payload) {
		return authRequest.get('ACCOUNT_API://plans?product=' + payload)
			.then(http.resolveData, http.rejectData);
	},

	getPlansV2(payload) {
		return authRequest.get('ACCOUNT_API://v2/plans?product=' + payload)
			.then(http.resolveData, http.rejectData)
			.then((plans) => {
				return plans.data;
			});
	},

	getScopePlansV2(payload) {
		return authRequest.get('ACCOUNT_API://v2/plans?product=' + payload)
			.then(http.resolveData, http.rejectData);
		// .then((plans) => {
		// 	return [
		// 		{
		// 		active: false,
		// 		amount: 0,
		// 		customBilling: false,
		// 		details: {},
		// 		hasTrial: false,
		// 		id: "pln_scope_app_base",
		// 		interval: "yearly",
		// 		name: "TekScope PC Waveform Analysis Software",
		// 		paymentMethodRequired: true
		// 	},
		// 	{
		// 		active: true,
		// 		amount: 16900,
		// 		customBilling: false,
		// 		details: {},
		// 		hasTrial: true,
		// 		id: "pln_scope_starter_yearly",
		// 		interval: "yearly",
		// 		name: "Starter",
		// 		description: "Base Package",
		// 		paymentMethodRequired: true
		// 	},
		// 	{
		// 		active: true,
		// 		amount: 36900,
		// 		customBilling: false,
		// 		details: {},
		// 		hasTrial: true,
		// 		id: "pln_scope_serial_decode_yearly",
		// 		interval: "yearly",
		// 		name: "Serial Decode",
		// 		description: "CAN, CAN FD, LIN, FlexRay, USB2.0, eUSB2.0, Ethernet, I3C, NRZ, SPMI, MDIO, SVID, SDLC, 8b/10b, Audio, MIPI C-PHY, MIPI D-PHY, Spacewire, Manchester, 1-Wire, SRCXPI, SRESPIt Power",
		// 		paymentMethodRequired: true
		// 	},
		// 	{
		// 		active: true,
		// 		amount: 36900,
		// 		customBilling: false,
		// 		details: {},
		// 		hasTrial: true,
		// 		id: "pln_scope_power_yearly",
		// 		interval: "yearly",
		// 		name: "Power",
		// 		description: "Advanced Power Analysis, Digital Power Management Analysis, Magnetic Analysis, Inverter & Motor Drive Analysis, IMDA DQ0, Serial Decode, SPMI, SVID",
		// 		paymentMethodRequired: true
		// 	},
		// 	{
		// 		active: true,
		// 		amount: 36900,
		// 		customBilling: false,
		// 		details: {},
		// 		hasTrial: true,
		// 		id: "pln_scope_milgov_yearly",
		// 		interval: "yearly",
		// 		name: "Mil/Gov",
		// 		description: "Jitter Analysis, Mask/Limit Testing, Inverter & Motor Drive Analysis, IMDA DQ0, Serial Decode, CAN, CAN FD, LIN, FlexRay, 100BASE-T1, SENT, PSI5, I3C",
		// 		paymentMethodRequired: true
		// 	},
		// 	{
		// 		active: true,
		// 		amount: 36900,
		// 		customBilling: false,
		// 		details: {},
		// 		hasTrial: true,
		// 		id: "pln_scope_automotive_yearly",
		// 		interval: "yearly",
		// 		name: "Automotive",
		// 		description: "Jitter Analysis, Mask/Limit Testing, Serial Decode, Mil-Std-1553, ARINC429, Spacewire, NRZ, Manchester",
		// 		paymentMethodRequired: true
		// 	},
		// 	{
		// 		active: true,
		// 		amount: 69000,
		// 		customBilling: false,
		// 		details: {},
		// 		hasTrial: true,
		// 		id: "pln_scope_ultimate_yearly",
		// 		interval: "yearly",
		// 		name: "Ultimate",
		// 		description: "Base Package, all premium packages and more",
		// 		paymentMethodRequired: true
		// 	}
		// ];
		// });
	},

	getPlanAddOns(planId) {
		return authRequest.get('ACCOUNT_API://plans/' + planId + '/addons')
			.then(http.resolveData, http.rejectData);
	},

	addPaymentMethod(payload) {
		return authRequest.post('ACCOUNT_API://me/payment_methods', payload)
			.then(http.resolveData, http.rejectData)
			.then(info => {
				return PaymentMethod.fromDTO(info);
			});
	},
	getEstimatedTax(zip) {
		if (zip == null) {
			return Promise.resolve();
		}
		return authRequest.get(`ACCOUNT_API://taxes/rate/${zip}`)
			.then(http.resolveData, http.rejectData);
	}

};



// WEBPACK FOOTER //
// ./src/modules/account-api.js